import { apiClient, setAuthService } from './api';
import { useAuthService } from './auth.service';

/**
 * Service for handling device-related API requests
 */
export const useDeviceService = () => {
  // Get auth service in component context and set it for API client
  const authService = useAuthService();
  setAuthService(authService);

  /**
   * Get all devices with optional pagination and filtering
   * @param {Object} options - Query options
   * @param {Number} options.page - Page number (default: 1)
   * @param {Number} options.limit - Items per page (default: 10)
   * @param {String} options.status - Filter by status
   * @param {String} options.sort - Sort field
   * @param {String} options.sortDirection - Sort direction (asc/desc)
   * @returns {Promise<Object>} Paginated devices response
   */
  const getDevices = async (options = {}) => {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (options.page) queryParams.append('page', options.page);
      if (options.limit) queryParams.append('limit', options.limit);
      if (options.status) queryParams.append('status', options.status);
      if (options.sort) queryParams.append('sort', options.sort);
      if (options.sortDirection) queryParams.append('sortDirection', options.sortDirection);
      
      const queryString = queryParams.toString();
      const url = `/devices${queryString ? `?${queryString}` : ''}`;
      
      const response = await apiClient.get(url);
      return response.data;
  };

  /**
   * Get a specific device by ID
   * @param {String} deviceId - Device ID (must be a MongoDB ObjectId string)
   * @returns {Promise<Object>} Device data
   */
  const getDeviceById = async (deviceId) => {
      const response = await apiClient.get(`/devices/${deviceId}`);
      return response.data;
  };

  /** Get archived daily or ranged device uptime from S3. */
  const getDeviceUptime = async (deviceId, options = {}) => {
      const filters = typeof options === 'string' ? { date: options } : options;
      const queryParams = new URLSearchParams();
      if (filters.date) queryParams.set('date', filters.date);
      if (filters.start) queryParams.set('start', filters.start);
      if (filters.end) queryParams.set('end', filters.end);
      const query = queryParams.toString() ? `?${queryParams}` : '';
      const response = await apiClient.get(`/devices/${deviceId}/uptime${query}`);
      return response.data;
  };

  /**
   * Update a device
   * @param {String} deviceId - Device ID (must be a MongoDB ObjectId string)
   * @param {Object} deviceData - Updated device data
   * @returns {Promise<Object>} Updated device
   */
  const updateDevice = async (deviceId, deviceData) => {
      const response = await apiClient.put(`/devices/${deviceId}`, deviceData);
      return response.data;
  };

  /**
   * Update device basic information (name, description)
   * @param {String} deviceId - Device ID
   * @param {Object} basicInfo - Basic info data
   * @param {String} basicInfo.name - Device name (required)
   * @param {String} basicInfo.description - Device description (optional)
   * @returns {Promise<Object>} Updated device
   */
  const updateDeviceBasicInfo = async (deviceId, basicInfo) => {
      const response = await apiClient.put(`/devices/${deviceId}/basic-info`, basicInfo);
      return response.data;
  };

  /**
   * Update device location information
   * @param {String} deviceId - Device ID
   * @param {Object} locationInfo - Location data
   * @param {String} locationInfo.address - Address (optional)
   * @param {String} locationInfo.city - City (optional)
   * @param {String} locationInfo.country - Country (optional)
   * @param {String} locationInfo.postalCode - Postal code (optional)
   * @param {Number} locationInfo.latitude - Latitude (optional)
   * @param {Number} locationInfo.longitude - Longitude (optional)
   * @returns {Promise<Object>} Updated device
   */
  const updateDeviceLocation = async (deviceId, locationInfo) => {
      const response = await apiClient.put(`/devices/${deviceId}/location`, locationInfo);
      return response.data;
  };

  /**
   * Update device connectivity settings
   * @param {String} deviceId - Device ID
   * @param {Object} connectivityInfo - Connectivity data
   * @param {Number} connectivityInfo.heartbeat_interval_minutes - Heartbeat interval in minutes (optional)
   * @returns {Promise<Object>} Updated device
   */
  const updateDeviceConnectivity = async (deviceId, connectivityInfo) => {
      const response = await apiClient.put(`/devices/${deviceId}/connectivity`, connectivityInfo);
      return response.data;
  };

  /**
   * Update device topics (ADMIN only)
   * @param {String} deviceId - Device ID
   * @param {Object} topicsInfo - Topics data
   * @param {Array<String>} topicsInfo.topics - Array of topic strings (required)
   * @returns {Promise<Object>} Updated device
   */
  const updateDeviceTopics = async (deviceId, topicsInfo) => {
      const response = await apiClient.put(`/devices/${deviceId}/topics`, topicsInfo);
      return response.data;
  };

  return {
    getDevices,
    getDeviceById,
    getDeviceUptime,
    updateDevice,
    updateDeviceBasicInfo,
    updateDeviceLocation,
    updateDeviceConnectivity,
    updateDeviceTopics
  };
};
